import React, {useEffect, useMemo, useState} from "react";
import {useParams, useNavigate, useSearchParams} from "react-router-dom";
import axios from "axios";
import {sanitizeLessonContent} from "../../../../utils/commonUtils";

import {ADMIN_API_BASE} from "../../../../utils/apiConfig";
import useUndoRedo from "../../../../hooks/useUndoRedo";

import JsonAdvancedEditor from "./shared/JsonAdvancedEditor";
import ConfirmModal from "../../../../components/modal/ConfirmModal";

import {
    ArrowRight,
    Pencil,
    Eye,
    Save,
    Undo2,
    Redo2,
    Code2,
} from "lucide-react";

import LessonFragmentsList from "./lesson/LessonFragmentsList";
import LessonBlockList from "./lesson/LessonBlockList";
import LessonBlockEditorPanel from "./lesson/LessonBlockEditorPanel";

import {makeStarterTemplate} from "./shared/starterTemplate";
import ErrorMessage from "../../../../components/layout/ErrorMessage";
import Button from "../../../../components/elements/Button";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowAltCircleLeft, faEye, faPencil, faSave} from "@fortawesome/free-solid-svg-icons";
import ButtonLink from "../../../../components/elements/ButtonLink";
import EditableField from "../../../../components/elements/EditableField";
import CustomSelect from "../../../../components/elements/CustomSelect";
import {lang} from "jalali-moment";
import ProjectEditor from "./project/ProjectEditor";
import {CONTENT_TYPE} from "../../../../utils/constants";
import LessonEditor from "./lesson/LessonEditor";
import ContentMetaEditor from "./shared/ContentMetaEditor";
import CustomSwitch from "../../../../components/elements/CustomSwitch";
/* ===================================================== */

const CourseContent = () => {
    const {courseId, contentId} = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [title, setTitle] = useState("");

    const currentLanguage =
        searchParams.get("language") || "FARSI";

    /* ===================== META ===================== */
    const handleMetaChange = (newMeta) => {
        setContentJson(prev => ({...prev, meta: newMeta}));
    };

    /* ===================== LANGUAGE ===================== */
    const [languagesOptions, setLanguagesOptions] = useState([]);

    useEffect(() => {
        fetchLanguages();
    }, [])

    const fetchLanguages = async () => {
        try {
            const token = await getCsrfToken();
            const res = await axios.get(
                `${ADMIN_API_BASE}/language/`,
                {
                    headers: {
                        'csrf-token': token,
                    },
                    withCredentials: true
                }
            );

            if (res.data.success) {
                const langs = res.data.languages.languages;
                setLanguagesOptions(langs.map(lang => lang.id));
            }
        } catch (err) {
            setError("خطا در دریافت محتوا");
        } finally {
            setLoading(false);
        }
    };

    /* ===================== CONTENT DETAILS ===================== */
    const [contentDetails, setContentDetails] = useState({xp: 0, coin: 0});
    const updateField = (field, value) => {
        setContentDetails((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const [isActive, setActive] = useState(false);
    const handleActiveChange = (value) => {
        setActive(value);
    }

    /* ===================== UI STATE ===================== */
    const [tab, setTab] = useState("edit");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [jsonModalOpen, setJsonModalOpen] = useState(false);

    const [contentMeta, setContentMeta] = useState(null);

    /* ===================== UNDO / REDO ===================== */
    const {
        present: contentJson,
        set: setContentJson,
        undo,
        redo,
        canUndo,
        canRedo,
        reset: resetHistory,
    } = useUndoRedo(null, {limit: 80});

    /* ===================== SELECTION ===================== */
    const [selectedFragmentId, setSelectedFragmentId] = useState(null);
    const [selectedBlockId, setSelectedBlockId] = useState(null);

    /* ===================== CONFIRM MODAL ===================== */
    const [confirm, setConfirm] = useState(null);

    /* ===================== CSRF ===================== */
    const getCsrfToken = async () => {
        const res = await fetch(`${ADMIN_API_BASE}/csrf-token`, {
            method: "GET",
            credentials: "include",
        });
        const data = await res.json();
        return data.csrfToken;
    };

    /* ===================== FETCH CONTENT ===================== */
    useEffect(() => {
        const fetchContent = async () => {
            try {
                const token = await getCsrfToken();

                setLoading(true);
                setError("");


                const res = await axios.get(
                    `${ADMIN_API_BASE}/course/${courseId}/content/${contentId}`,
                    {
                        params: {
                            language: currentLanguage,
                        },
                        headers: {
                            "csrf-token": token,
                        },
                        withCredentials: true,
                    }
                );

                const row = res.data.content ?? res.data;
                const rowType = row.type; // usually 0/1

                // Set lesson/project title
                if (row.title !== null) {
                    setTitle(row.title);
                }

                const normalizedType =
                    rowType === 0 || rowType === "0" || rowType === "lesson"
                        ? "lesson"
                        : "project";

                const jsonRaw =
                    row.content && Object.keys(row.content).length > 0
                        ? row.content
                        : makeStarterTemplate({
                            contentId,
                            type:
                                normalizedType === "lesson"
                                    ? CONTENT_TYPE.LESSON
                                    : CONTENT_TYPE.PROJECT,
                        });

                const json =
                    normalizedType === "lesson"
                        ? sanitizeLessonContent(jsonRaw)
                        : jsonRaw;

                setContentMeta({
                    updatedAt: row.updated_at,
                    type: normalizedType, // <-- string now
                });

                setContentDetails({xp: row.xp, coin: row.coin});
                setActive(row.is_active);
                resetHistory(json);

            } catch (err) {
                console.error(err);
                setError("خطا در دریافت محتوا");
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, [courseId, contentId, currentLanguage, resetHistory]);

    useEffect(() => {
        if (!contentJson || contentJson.type !== "lesson") {
            return;
        }
        const fragments = contentJson.fragments || [];

        if (fragments.length === 0) {
            setSelectedFragmentId(null);
            setSelectedBlockId(null);
            return;
        }

        const safeFragment =
            fragments.find((f) => f.id === selectedFragmentId) || fragments[0];

        if (safeFragment.id !== selectedFragmentId) {
            setSelectedFragmentId(safeFragment.id);
        }

        const blocks = safeFragment.blocks || [];
        const safeBlock =
            blocks.find((b) => b.id === selectedBlockId) || blocks[0] || null;

        if ((safeBlock?.id || null) !== selectedBlockId) {
            setSelectedBlockId(safeBlock?.id || null);
        }
    }, [contentJson, selectedFragmentId, selectedBlockId]);

    /* ===================== KEYBOARD UNDO / REDO ===================== */
    useEffect(() => {
        const onKeyDown = (e) => {
            const isMac = navigator.platform.toLowerCase().includes("mac");
            const mod = isMac ? e.metaKey : e.ctrlKey;
            if (!mod) return;

            if (e.key.toLowerCase() === "z" && !e.shiftKey) {
                e.preventDefault();
                undo();
            }

            if (
                (e.key.toLowerCase() === "z" && e.shiftKey) ||
                e.key.toLowerCase() === "y"
            ) {
                e.preventDefault();
                redo();
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [undo, redo]);

    /* ===================== MEMOS ===================== */
    const selectedFragment = useMemo(() => {
        const fragments = Array.isArray(contentJson?.fragments) ? contentJson.fragments : [];
        return fragments.find((f) => f && f.id === selectedFragmentId) || null;
    }, [contentJson, selectedFragmentId]);

    const selectedBlock = useMemo(() => {
        const blocks = Array.isArray(selectedFragment?.blocks) ? selectedFragment.blocks : [];
        return blocks.find((b) => b && b.id === selectedBlockId) || null;
    }, [selectedFragment, selectedBlockId]);


    /* ===================== HELPERS ===================== */
    const updateFragment = (fragmentId, updater) => {
        setContentJson((prev) => {
            const next = structuredClone(prev);
            next.fragments = next.fragments || [];

            const idx = next.fragments.findIndex((f) => f.id === fragmentId);
            if (idx === -1) return prev;

            next.fragments[idx] = updater(next.fragments[idx]);
            return next;
        });
    };

    const updateBlock = (fragmentId, blockId, updater) => {
        updateFragment(fragmentId, (frag) => {
            const next = structuredClone(frag);
            next.blocks = next.blocks || [];
            const idx = next.blocks.findIndex((b) => b.id === blockId);
            if (idx === -1) return frag;
            next.blocks[idx] = updater(next.blocks[idx]);
            return next;
        });
    };

    /* ===================== SAVE ===================== */
    const handleSave = async () => {
        try {
            setSaving(true);
            setError("");


            const type = contentJson?.type || contentMeta?.type || "lesson";

            let payloadContent;

            if (type === "lesson") {
                payloadContent = {
                    id: contentJson?.id || contentId,
                    schemaVersion: contentJson?.schemaVersion ?? 1,
                    type: "lesson",
                    meta: {
                        energyCost: contentJson?.meta?.energyCost ?? 0,
                    },
                    fragments: contentJson?.fragments ?? [],
                };
            } else if (type === "project") {
                payloadContent = {
                    id: contentJson?.id || contentId,
                    schemaVersion: contentJson?.schemaVersion ?? 1,
                    type: "project",

                    workspace: contentJson.workspace,

                    meta: {
                        energyCost: contentJson?.meta?.energyCost ?? 0,
                    },

                    parts: contentJson?.parts ?? []
                };
            }


            const token = await getCsrfToken();



            const res = await axios.patch(
                `${ADMIN_API_BASE}/course/${courseId}/content/${contentId}/`,
                {
                    content: payloadContent,
                    title: title,
                    language: currentLanguage
                },
                {
                    params: {language: currentLanguage},
                    headers: {
                        'csrf-token': token,
                    },
                    withCredentials: true
                }
            );

            if (res.data?.success) {
                const savedContent =
                    res.data.content.type === "lesson"
                        ? sanitizeLessonContent(res.data.content)
                        : res.data.content;

                setContentJson(savedContent);

                if (typeof resetHistory === "function") {
                    resetHistory(savedContent);
                }
            } else {
                setError(res.data?.message || "ذخیره انجام نشد");
            }
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                "ذخیره انجام نشد"
            );
        } finally {
            setSaving(false);
        }
    };


    const handleContentDetailsSave = async () => {
        try {
            setSaving(true);
            setError("");

            const csrf = await getCsrfToken();
            const res = await axios.patch(
                `${ADMIN_API_BASE}/course/${courseId}/content/${contentId}/details`,
                {xp: contentDetails.xp, coin: contentDetails.coin, is_active: isActive},
                {
                    withCredentials: true,
                    headers: {"csrf-token": csrf},
                }
            );
            setContentDetails({xp: res.data.xp, coin: res.data.coin});
            setActive(res.data.is_active);
        } catch (err) {
            console.error(err);
            setError("ذخیره انجام نشد");
        } finally {
            setSaving(false);
        }
    }

    /* ===================== DELETE ===================== */
    const confirmDelete = () => {
        if (!confirm) return;

        if (confirm.type === "fragment") {
            setContentJson((prev) => ({
                ...prev,
                fragments: prev.fragments.filter((f) => f.id !== confirm.id),
            }));
        }

        if (confirm.type === "block") {
            updateFragment(selectedFragmentId, (frag) => ({
                ...frag,
                blocks: frag.blocks.filter((b) => b.id !== confirm.id),
            }));
        }

        setConfirm(null);
    };

    /* ===================== RENDER ===================== */
    if (loading) {
        return <div className="p-6">در حال بارگذاری...</div>;
    }

    return (
        <div className="min-h-screen p-6 bg-gray-100">
            {/* ================= HEADER ================= */}
            <div className="mb-6 space-y-4">
                <ErrorMessage message={error} type="error"/>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-5">
                        <Button
                            onClick={() =>
                                navigate(`/admin/course/${courseId}/sections/`)
                            }
                            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/70 backdrop-blur border">
                            <ArrowRight size={18}/>
                            <span>بازگشت</span>
                        </Button>

                        <div className="flex items-center gap-3">
                            {
                                languagesOptions && languagesOptions.length > 0 && <CustomSelect onChange={(e) => {
                                    setSearchParams({
                                        language: e.target.value,
                                    });

                                }} options={languagesOptions} title={'LANGUAGE'} value={currentLanguage}/>
                            }
                        </div>
                    </div>


                    <div className="flex items-center gap-2">
                        <Button
                            onClick={undo}
                            disabled={!canUndo}
                            className="px-3 py-2 rounded-xl border bg-white disabled:opacity-40">
                            <Undo2 size={18}/>
                        </Button>

                        <Button
                            onClick={redo}
                            disabled={!canRedo}
                            className="px-3 py-2 rounded-xl border bg-white disabled:opacity-40">
                            <Redo2 size={18}/>
                        </Button>

                        <Button
                            onClick={() => setJsonModalOpen(true)}
                            className="px-3 py-2 rounded-xl border bg-white flex items-center gap-2">
                            <Code2 size={18}/>
                            <span>JSON</span>
                        </Button>
                        <ButtonLink
                            to={`/admin/course/${courseId}/content/${contentId}/preview/`}
                            className="px-4 py-2 gap-x-3 rounded-xl text-white bg-blue-600 disabled:bg-gray-400">
                            <FontAwesomeIcon icon={faEye} size={'lg'}/>
                            <span>نمایش</span>
                        </ButtonLink>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-4 py-2 rounded-xl text-white bg-blue-600 disabled:bg-gray-400">
                            <Save size={18}/>
                            <span>ذخیره</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* ================= DETAILS ================= */}
            <div className="mb-6 flex items-center justify-end gap-x-5">
                <CustomSwitch onChange={(value) => {
                    handleActiveChange(value);
                }} checked={isActive} label={'فعال بودن'} />

                <EditableField
                    value={contentDetails.xp}
                    suffix="xp"
                    type={'number'}
                    onSave={(value) => updateField("xp", value)}
                />

                <EditableField
                    value={contentDetails.coin}
                    suffix="سکه"
                    type={'number'}
                    onSave={(value) => updateField("coin", value)}
                />

                <Button onClick={handleContentDetailsSave}>
                    <FontAwesomeIcon icon={faSave} size={'lg'}/>
                </Button>
            </div>

            {/* ================= META ================= */}
            <div className="py-5 gap-8 mb-10 flex items-center justify-end">
                <EditableField
                    value={contentJson?.meta?.energyCost || 0}
                    type="number"
                    onChange={handleMetaChange}
                    onSave={(val) =>
                        setContentJson(prev => ({
                            ...prev,
                            meta: {...(prev?.meta || {}), energyCost: parseInt(val, 10) || 0}
                        }))
                    }
                />

                <EditableField
                    value={title || ""}
                    onSave={(value) =>
                        setTitle(value)
                    }
                />
            </div>

            {/* ================= BODY ================= */}

            {contentMeta?.type === "lesson" ? (
                <LessonEditor
                    courseId={courseId}
                    contentJson={contentJson}
                    selectedFragmentId={selectedFragmentId}
                    selectedBlockId={selectedBlockId}
                    selectedFragment={selectedFragment}
                    selectedBlock={selectedBlock}
                    setSelectedFragmentId={setSelectedFragmentId}
                    setSelectedBlockId={setSelectedBlockId}
                    setContentJson={setContentJson}
                    updateFragment={updateFragment}
                    updateBlock={updateBlock}
                    setConfirm={setConfirm}
                />
            ) : (
                <ProjectEditor contentJson={contentJson} setContentJson={setContentJson}/>
            )}


            {/* ================= MODALS ================= */}
            <ConfirmModal
                open={!!confirm}
                message={confirm?.message}
                onConfirm={confirmDelete}
                onCancel={() => setConfirm(null)}
            />

            <JsonAdvancedEditor
                open={jsonModalOpen}
                title={`Advanced JSON • ${contentId}`}
                initialObject={contentJson}
                schemaVersionExpected={1}
                onClose={() => setJsonModalOpen(false)}
                onApply={(parsed) => setContentJson(parsed)}
            />

        </div>
    );
};

export default CourseContent;
